import React, { useMemo } from "react";
import { IconArrowLongLeft, IconArrowLongRight } from "../../../components/icons";

interface PaginationProps {
  totalItems: number;
  step: number;
  page: number;
  setPage: (page: number) => void;
}

const Pagination = ({ totalItems, step, page, setPage }: PaginationProps) => {
  const totalPages = Math.ceil(totalItems / step);

  const activePage = (p: number) =>
    p === page
      ? "bg-slate-800 text-white rounded-md mx-1 shadow-md"
      : "border border-slate-300 text-slate-600 rounded-md mx-1 hover:bg-slate-800 hover:text-white";

  const visiblePages = useMemo(() => {
    if (totalPages <= 20) {
      return Array.from({ length: totalPages }, (_, i) => i + 1)
    }

    // début (pages 1 → 10)
    if (page <= 10) {
      return [...Array.from({ length: 20 }, (_, i) => i + 1), "...", totalPages]
    }

    // fin (dernières 20 pages)
    if (page >= totalPages - 10) {
      return [1, "...", ...Array.from({ length: 20 }, (_, i) => totalPages - 19 + i)]
    }

    // milieu (fenêtre centrée sur la page courante)
    const start = page - 9;
    return [1, "...", ...Array.from({ length: 20 }, (_, i) => start + i), "...", totalPages]
  }, [page, totalPages]);

  if (totalPages <= 1) return null;

  return (
    <div className="flex justify-center">
      {/* bouton précédent */}
      <button
        disabled={page === 1}
        onClick={() => setPage(page - 1)}
        className={`w-10 h-10 rounded-md border border-slate-300 flex items-center justify-center text-xs text-slate-600 mx-1 ${page === 1 ? 'disabled:opacity-50' : 'hover:bg-slate-800 hover:text-white'}`}
      >
        <IconArrowLongLeft />
      </button>

      {/* pages */}
      {visiblePages.map((p, idx) =>
        p === "..." ? (
          <span key={`${p}-${idx}`} className="px-2">...</span>
        ) : (
          <button
            key={`${p}-${idx}`}
            onClick={() => setPage(p as number)}
            className={`w-10 h-10 rounded-md border border-slate-300 flex items-center justify-center text-xs ${activePage(p as number)}`}
          >
            {p}
          </button>
        )
      )}

      {/* bouton suivant */}
      <button
        disabled={page === totalPages}
        onClick={() => setPage(page + 1)}
        className={`w-10 h-10 rounded-md border border-slate-300 flex items-center justify-center text-xs text-slate-600 mx-1 ${page === totalPages ? 'disabled:opacity-50' : 'hover:bg-slate-800 hover:text-white'}`}
      >
        <IconArrowLongRight />
      </button>
    </div>
  );
};

export default Pagination;
